import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { CheckCircle2, Leaf, AlertCircle, Loader2 } from "lucide-react";

/** Standalone public client (no auth header) so we don't leak tokens. */
const PUBLIC = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL + "/api",
});

const RELATIONSHIP_OPTIONS = [
  ["direct_manager", "Direct Manager"],
  ["team_lead", "Team Lead / Supervisor"],
  ["hr_manager", "HR Manager"],
  ["colleague", "Colleague"],
  ["client", "Client / Customer"],
  ["other", "Other"],
];
const LENGTH_OPTIONS = [
  ["lt_6m", "Less than 6 months"],
  ["6m_1y", "6 months – 1 year"],
  ["1_2y", "1 – 2 years"],
  ["2_5y", "2 – 5 years"],
  ["gt_5y", "More than 5 years"],
];
const FREQUENCY = [["regularly", "Yes, regularly"], ["occasionally", "Yes, occasionally"], ["no", "No"]];
const REEMPLOY = [
  ["definitely_yes", "Definitely yes"],
  ["probably_yes", "Probably yes"],
  ["unsure", "Unsure"],
  ["probably_no", "Probably not"],
  ["definitely_no", "Definitely not"],
];
const RATING_LABELS = {
  rating_timekeeping: "Timekeeping & Punctuality",
  rating_attendance: "Attendance & Reliability",
  rating_sickness: "Sickness Absence",
  rating_work_ability: "Work Ability & Performance",
  rating_trustworthiness: "Trustworthiness & Reliability",
  rating_conduct: "General Conduct & Professionalism",
  rating_honesty: "Honesty & Integrity",
};
const RATING_DESC = {
  rating_sickness: ["Frequent", "Regular", "Average", "Good", "Excellent"],
};
const DEFAULT_RATING_DESC = ["Poor", "Fair", "Good", "Very Good", "Excellent"];

export default function RefereePortalPage() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    referee_name: "", referee_position: "", referee_company: "",
    referee_company_address1: "", referee_company_address2: "",
    referee_city: "", referee_postcode: "", referee_phone: "", referee_email: "",
    relationship: "", relationship_other: "", length_known: "",
    applicant_position: "",
    work_with_elderly_vulnerable: "", work_with_children: "",
    responsibilities: "", strengths: "",
    rating_timekeeping: 0, rating_attendance: 0, rating_sickness: 0,
    rating_work_ability: 0, rating_trustworthiness: 0, rating_conduct: 0, rating_honesty: 0,
    reason_for_leaving: "", would_reemploy: "",
    disciplinary_action: false, disciplinary_details: "",
    safeguarding_concerns: false, safeguarding_details: "",
    additional_comments: "",
    signature_name: "", signed_on: new Date().toISOString().slice(0, 10),
    agree_terms: false,
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    PUBLIC.get(`/references/portal/${token}`)
      .then(({ data }) => {
        setCtx(data);
        if (data.referee_name) set("referee_name", data.referee_name);
        if (data.referee_email) set("referee_email", data.referee_email);
        if (data.already_submitted) setSubmitted(true);
      })
      .catch((e) => {
        const status = e?.response?.status;
        setErr({
          status,
          detail: e?.response?.data?.detail || "Invalid link",
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validate = () => {
    const required = [
      "referee_name", "referee_position", "referee_company", "referee_email",
      "relationship", "length_known", "applicant_position",
      "work_with_elderly_vulnerable", "work_with_children",
      "responsibilities", "strengths",
      "reason_for_leaving", "would_reemploy",
      "additional_comments", "signature_name", "signed_on",
    ];
    for (const k of required) {
      if (!form[k] || String(form[k]).trim() === "") {
        toast.error(`Please complete: ${k.replace(/_/g, " ")}`);
        return false;
      }
    }
    for (const k of Object.keys(RATING_LABELS)) {
      if (!form[k] || form[k] < 1 || form[k] > 5) {
        toast.error(`Please rate: ${RATING_LABELS[k]}`);
        return false;
      }
    }
    if (form.additional_comments.length < 50) {
      toast.error("Additional Comments must be at least 50 characters");
      return false;
    }
    if (!form.agree_terms) {
      toast.error("Please tick the declaration box to submit");
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      await PUBLIC.post(`/references/portal/${token}`, form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (err) {
    const isExpired = err.status === 410;
    const isNotFound = err.status === 404;
    return (
      <Container>
        <Seo title="Reference link | Rabah Concept" noindex />
        <div className="cc-card p-8 max-w-xl mx-auto text-center" data-testid="referee-error-card">
          <AlertCircle className="w-10 h-10 text-[var(--cc-accent)] mx-auto mb-3" />
          <h1 className="font-display text-2xl font-bold mb-2" data-testid="referee-error-heading">
            {isExpired ? "This reference link has expired" : isNotFound ? "This reference link is not recognised" : "This reference link is no longer valid"}
          </h1>
          <p className="text-[var(--cc-text-soft)]" data-testid="referee-error-detail">{err.detail}</p>
          <p className="text-xs text-[var(--cc-text-soft)] mt-4">If you believe this is an error, please contact recruitment@rabahconcept.co.uk and we&apos;ll resend a fresh link.</p>
        </div>
      </Container>
    );
  }
  if (!ctx) {
    return <Container><div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="w-6 h-6 animate-spin" /></div></Container>;
  }
  if (submitted) {
    return (
      <Container>
        <Seo title="Reference received | Rabah Concept" noindex />
        <div className="cc-card p-10 max-w-xl mx-auto text-center" data-testid="referee-submitted">
          <CheckCircle2 className="w-12 h-12 text-[var(--cc-primary)] mx-auto mb-4" />
          <h1 className="font-display text-3xl font-bold mb-2">Thank you</h1>
          <p className="text-[var(--cc-text-soft)] mb-2">Your reference has been securely received by Rabah Concept Recruitment.</p>
          <p className="text-sm text-[var(--cc-text-soft)]">A confirmation has been emailed to you. If we need any clarification we will be in touch.</p>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Seo title={`Reference for ${ctx.candidate_first_name} | Rabah Concept`} noindex />
      <div className="max-w-3xl mx-auto pb-32">
        <div className="cc-card p-7 mb-6">
          <h1 className="font-display text-3xl font-bold mb-1">Reference for {ctx.candidate_name}</h1>
          <p className="text-[var(--cc-text-soft)]">Position: <strong>{ctx.job_title}</strong>{ctx.job_location ? ` · ${ctx.job_location}` : ""}</p>
          <p className="text-sm text-[var(--cc-text-soft)] mt-2">
            Thank you for agreeing to provide a reference. This secure form takes 10–15 minutes. Your response is kept confidential and used solely for employment assessment.
          </p>
        </div>

        <Section title="1. Your details">
          <Grid>
            <F label="Your full name *"><Input value={form.referee_name} onChange={(v) => set("referee_name", v)} testid="ref-name" /></F>
            <F label="Position / title *"><Input value={form.referee_position} onChange={(v) => set("referee_position", v)} testid="ref-position" /></F>
            <F label="Company name *"><Input value={form.referee_company} onChange={(v) => set("referee_company", v)} testid="ref-company" /></F>
            <F label="Email *"><Input type="email" value={form.referee_email} onChange={(v) => set("referee_email", v)} testid="ref-email" /></F>
            <F label="Phone (optional)"><Input value={form.referee_phone} onChange={(v) => set("referee_phone", v)} /></F>
            <F label="Company address line 1"><Input value={form.referee_company_address1} onChange={(v) => set("referee_company_address1", v)} /></F>
            <F label="Company address line 2"><Input value={form.referee_company_address2} onChange={(v) => set("referee_company_address2", v)} /></F>
            <F label="City"><Input value={form.referee_city} onChange={(v) => set("referee_city", v)} /></F>
            <F label="Postcode"><Input value={form.referee_postcode} onChange={(v) => set("referee_postcode", v)} /></F>
          </Grid>
        </Section>

        <Section title="2. Relationship to candidate">
          <F label="Your relationship to the candidate *">
            <Select value={form.relationship} onChange={(v) => set("relationship", v)} options={RELATIONSHIP_OPTIONS} testid="ref-relationship" />
          </F>
          {form.relationship === "other" && (
            <F label="Please specify"><Input value={form.relationship_other} onChange={(v) => set("relationship_other", v)} /></F>
          )}
          <F label="Length of time known the candidate *">
            <Select value={form.length_known} onChange={(v) => set("length_known", v)} options={LENGTH_OPTIONS} testid="ref-length" />
          </F>
          <F label="Candidate's position during employment *"><Input value={form.applicant_position} onChange={(v) => set("applicant_position", v)} /></F>
        </Section>

        <Section title="3. Work experience & suitability">
          <F label="Did this role involve work with elderly or vulnerable adults? *">
            <RadioGroup value={form.work_with_elderly_vulnerable} onChange={(v) => set("work_with_elderly_vulnerable", v)} options={FREQUENCY} name="vul" />
          </F>
          <F label="Did this role involve work with children (0–17 years)? *">
            <RadioGroup value={form.work_with_children} onChange={(v) => set("work_with_children", v)} options={FREQUENCY} name="chd" />
          </F>
          <F label="Main responsibilities in this role *"><Textarea value={form.responsibilities} onChange={(v) => set("responsibilities", v)} /></F>
          <F label="Key strengths & qualities *"><Textarea value={form.strengths} onChange={(v) => set("strengths", v)} /></F>
        </Section>

        <Section title="4. Competency ratings (1 Poor – 5 Excellent)">
          {Object.entries(RATING_LABELS).map(([k, lbl]) => (
            <RatingRow key={k} label={lbl} value={form[k]} onChange={(v) => set(k, v)}
                       descriptors={RATING_DESC[k] || DEFAULT_RATING_DESC} testid={`rating-${k}`} />
          ))}
        </Section>

        <Section title="5. Employment history & conduct">
          <F label="Reason for leaving position *"><Input value={form.reason_for_leaving} onChange={(v) => set("reason_for_leaving", v)} /></F>
          <F label="Would you re-employ this candidate? *">
            <RadioGroup value={form.would_reemploy} onChange={(v) => set("would_reemploy", v)} options={REEMPLOY} name="reemploy" />
          </F>
          <F label="Has the candidate been subject to any disciplinary action? *">
            <YesNo value={form.disciplinary_action} onChange={(v) => set("disciplinary_action", v)} name="disc" />
          </F>
          {form.disciplinary_action && (
            <F label="Please explain"><Textarea value={form.disciplinary_details} onChange={(v) => set("disciplinary_details", v)} /></F>
          )}
          <F label="Are you aware of any safeguarding concerns or issues? *">
            <YesNo value={form.safeguarding_concerns} onChange={(v) => set("safeguarding_concerns", v)} name="safe" />
          </F>
          {form.safeguarding_concerns && (
            <F label="Please explain"><Textarea value={form.safeguarding_details} onChange={(v) => set("safeguarding_details", v)} /></F>
          )}
        </Section>

        <Section title="6. Additional information">
          <F label={`Additional comments * (min 50 characters — ${form.additional_comments.length})`}>
            <Textarea value={form.additional_comments} onChange={(v) => set("additional_comments", v)} minHeight={120} testid="ref-additional" />
          </F>
        </Section>

        <Section title="7. Signature & declaration">
          <p className="text-sm text-[var(--cc-text-soft)] mb-3">
            I confirm that the information provided is true and accurate to the best of my knowledge.
            I understand this information will be kept confidential and used solely for employment purposes.
          </p>
          <Grid>
            <F label="Type your full name as signature *"><Input value={form.signature_name} onChange={(v) => set("signature_name", v)} testid="ref-signature" /></F>
            <F label="Date *"><Input type="date" value={form.signed_on} onChange={(v) => set("signed_on", v)} /></F>
          </Grid>
          <label className="flex items-center gap-2 text-sm mt-3 cursor-pointer">
            <input type="checkbox" checked={form.agree_terms} onChange={(e) => set("agree_terms", e.target.checked)} data-testid="ref-agree" />
            I agree to the declaration above.
          </label>
        </Section>

        <div className="flex gap-3 mt-6">
          <button className="btn-primary text-base py-3 px-6" onClick={submit} disabled={busy} data-testid="ref-submit">
            {busy ? "Submitting…" : "Submit reference"}
          </button>
        </div>
      </div>
    </Container>
  );
}

/* ---- presentational helpers ---- */
function Container({ children }) {
  return (
    <div className="min-h-screen px-6 py-10" style={{ background: "var(--cc-bg)" }}>
      <header className="max-w-3xl mx-auto mb-8 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--cc-primary)" }}>
          <Leaf className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="font-display font-bold text-lg">Rabah Concept Recruitment</div>
          <div className="text-xs text-[var(--cc-text-soft)]">Secure referee portal</div>
        </div>
      </header>
      {children}
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div className="cc-card p-7 mb-6">
      <h2 className="font-display text-xl font-bold mb-4">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Grid({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>; }
function F({ label, children }) {
  return <div><label className="label-eyebrow block mb-1">{label}</label>{children}</div>;
}
function Input({ value, onChange, type = "text", testid }) {
  return <input className="cc-input" type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
}
function Textarea({ value, onChange, minHeight = 90, testid }) {
  return <textarea className="cc-textarea" style={{ minHeight }} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
}
function Select({ value, onChange, options, testid }) {
  return (
    <select className="cc-select" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      <option value="">— Select —</option>
      {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
    </select>
  );
}
function RadioGroup({ value, onChange, options, name }) {
  return (
    <div className="flex flex-wrap gap-3">
      {options.map(([v, label]) => (
        <label key={v} className={`px-3 py-2 rounded-lg border cursor-pointer text-sm ${value === v ? "bg-[var(--cc-primary)] text-white border-[var(--cc-primary)]" : "border-[var(--cc-border)]"}`}>
          <input type="radio" name={name} value={v} className="hidden" checked={value === v} onChange={() => onChange(v)} />
          {label}
        </label>
      ))}
    </div>
  );
}
function YesNo({ value, onChange, name }) {
  return <RadioGroup name={name} value={value === true ? "yes" : value === false ? "no" : ""} onChange={(v) => onChange(v === "yes")} options={[["yes", "Yes"], ["no", "No"]]} />;
}
function RatingRow({ label, value, onChange, descriptors, testid }) {
  return (
    <div className="border-b border-[var(--cc-border)] last:border-0 pb-3">
      <div className="text-sm font-medium mb-1.5">{label}</div>
      <div className="flex gap-2 flex-wrap" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)}
                  className={`px-3 py-2 rounded-lg border text-xs cursor-pointer transition-all ${value === n ? "bg-[var(--cc-primary)] text-white border-[var(--cc-primary)]" : "border-[var(--cc-border)] text-[var(--cc-text-soft)] hover:border-[var(--cc-primary)]"}`}
                  data-testid={`${testid}-${n}`}>
            {n} · {descriptors[n - 1]}
          </button>
        ))}
      </div>
    </div>
  );
}
